import type { Size } from './types';

type StageIconProps = {
  color: string | null | undefined;
  order: number | null | undefined;
  size?: Size;
};

/** Returns true if a hex color is light enough to need dark text */
export function isLightColor(hex: string | null | undefined): boolean {
  if (!hex) return false;
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // Perceived luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55;
}

function parseHex(hex: string): [number, number, number] | null {
  const c = hex.replace('#', '');
  if (c.length < 6) return null;
  return [parseInt(c.substring(0, 2), 16), parseInt(c.substring(2, 4), 16), parseInt(c.substring(4, 6), 16)];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

const WHITE_LUMINANCE = 1;

function whiteContrastRatio(rgb: [number, number, number]): number {
  const L = relativeLuminance(rgb);
  return (WHITE_LUMINANCE + 0.05) / (L + 0.05);
}

const toHex = (n: number) => n.toString(16).padStart(2, '0');

/** If white text on `hex` is below the AA-large threshold (3:1), darken the
 *  color uniformly so the hue is preserved but white text becomes legible.
 *  Used when a stage colour like lime green is too light for white text but
 *  not light enough to trigger dark text via `isLightColor`. */
export function darkenForWhiteContrast(hex: string | null | undefined, minRatio = 3): string | null | undefined {
  if (!hex) return hex;
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  if (whiteContrastRatio(rgb) >= minRatio) return hex;

  // Scale all channels uniformly until contrast hits the threshold. Step is
  // small enough to keep hue/saturation stable in eye-perception terms.
  let factor = 0.95;
  while (factor > 0.1) {
    const candidate: [number, number, number] = [
      Math.round(rgb[0] * factor),
      Math.round(rgb[1] * factor),
      Math.round(rgb[2] * factor),
    ];
    if (whiteContrastRatio(candidate) >= minRatio) {
      return `#${toHex(candidate[0])}${toHex(candidate[1])}${toHex(candidate[2])}`;
    }
    factor -= 0.05;
  }
  // Fallback: black background
  return '#000000';
}

export function stageTextSize(size: Size = 'md') {
  return {
    '3xs': 'text-[6px]',
    '2xs': 'text-[7px]',
    xs: 'text-[8px]',
    sm: 'text-[9px]',
    md: 'text-[11px]',
    lg: 'text-xs',
    xl: 'text-sm',
    '2xl': 'text-base',
    '3xl': 'text-lg',
    '4xl': 'text-xl',
  }[size];
}

export default function StageIcon({ color, order, size = 'md' }: StageIconProps) {
  const light = isLightColor(color);
  // When using white text, ensure the colour is dark enough for ≥3:1 contrast.
  // Light colours stay as-is and use dark text instead.
  const effectiveColor = light ? color : darkenForWhiteContrast(color) ?? color;
  return (
    <div
      style={{ background: effectiveColor ?? undefined }}
      className={`flex size-full items-center justify-center rounded-full font-bold ${light ? 'text-black/70' : 'text-white'} ${stageTextSize(size)}`}>
      {order}
    </div>
  );
}
